import { getSupabase } from '$db/remote/supabase';
import { getGuestIdentity } from './guest';
import type { Identity } from './types';

export interface AuthCredentials {
  email: string;
  password: string;
}

export async function getCurrentIdentity(): Promise<Identity> {
  const supabase = getSupabase();
  if (!supabase) {
    return getGuestIdentity();
  }
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session) {
    return getGuestIdentity();
  }
  const user = data.session.user;
  return {
    kind: 'authenticated',
    userId: user.id,
    email: user.email ?? null,
    displayName:
      (user.user_metadata?.display_name as string | undefined) ?? null
  };
}

export async function signUp(credentials: AuthCredentials): Promise<Identity> {
  const supabase = getSupabase();
  if (!supabase) {
    throw new Error('Supabase no está configurado. Rellena .env primero.');
  }
  const { data, error } = await supabase.auth.signUp({
    email: credentials.email,
    password: credentials.password
  });
  if (error) throw error;
  const user = data.user;
  if (!user) {
    throw new Error('signUp no devolvió usuario.');
  }
  return {
    kind: 'authenticated',
    userId: user.id,
    email: user.email ?? null,
    displayName: null
  };
}

export async function signIn(credentials: AuthCredentials): Promise<Identity> {
  const supabase = getSupabase();
  if (!supabase) {
    throw new Error('Supabase no está configurado. Rellena .env primero.');
  }
  const { data, error } = await supabase.auth.signInWithPassword({
    email: credentials.email,
    password: credentials.password
  });
  if (error) throw error;
  const user = data.user;
  if (!user) {
    throw new Error('signIn no devolvió usuario.');
  }
  return {
    kind: 'authenticated',
    userId: user.id,
    email: user.email ?? null,
    displayName: null
  };
}

export async function signOut(): Promise<void> {
  const supabase = getSupabase();
  if (!supabase) return;
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function resetPassword(email: string): Promise<void> {
  const supabase = getSupabase();
  if (!supabase) throw new Error('Supabase no está configurado.');
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: window.location.origin, // Supabase redirigirá aquí con el token
  });
  if (error) throw error;
}

export async function updatePassword(newPassword: string): Promise<void> {
  const supabase = getSupabase();
  if (!supabase) throw new Error('Supabase no está configurado.');
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
}

export function onAuthChange(
  handler: (identity: Identity | null) => void
): () => void {
  const supabase = getSupabase();
  if (!supabase) return () => undefined;
  const { data } = supabase.auth.onAuthStateChange((_event, session) => {
    if (!session) {
      handler(null);
      return;
    }
    const user = session.user;
    handler({
      kind: 'authenticated',
      userId: user.id,
      email: user.email ?? null,
      displayName:
        (user.user_metadata?.display_name as string | undefined) ?? null
    });
  });
  return () => data.subscription.unsubscribe();
}